import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Tooltip } from '@chakra-ui/react';
import { apiUrl } from '../api/config';
import { useChatUi } from '../ChatContext';
import { sourceLocation } from './CitationChip';

/** Markdown of the answer plus a numbered reference list of the cited pages. */
export function answerWithReferences(message) {
  const text = message.text || '';
  const ids = message.citedIds && message.citedIds.length ? message.citedIds : [];
  const refs = ids
    .map((id) => message.sourceById[id])
    .filter(Boolean)
    .map((s) => `[${s.id}] ${[sourceLocation(s), s.section, s.section_title || s.title].filter(Boolean).join(' ')}`);
  const notice = message.notice && message.notice.text ? `\n\n${message.notice.text}` : '';
  return refs.length ? `${text}\n\n---\n${refs.join('\n')}${notice}` : `${text}${notice}`;
}

/** Plain text for speech: markdown symbols, citation markers and table pipes removed. */
export function textForSpeech(text) {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/\[\d{1,2}(?:\s*[,、]\s*\d{1,2})*\]/g, '')
    .replace(/^\s*\|?\s*:?-{2,}.*$/gm, '')
    .replace(/[#*_`|>【】・]/g, ' ')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

let playing = null; // only one answer reads aloud at a time

const ICON = {
  up: 'M7 10v12M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z',
  down: 'M17 14V2M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z',
  copy: 'M8 4h10a2 2 0 0 1 2 2v12M16 8H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2z',
  check: 'M20 6 9 17l-5-5',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  speak: 'M11 5 6 9H2v6h4l5 4V5zM15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14',
  stop: 'M6 6h12v12H6z',
};

function ActionButton({ label, icon, onClick, active, busy }) {
  return (
    <Tooltip label={label} openDelay={300} hasArrow>
      <button type="button" className={`cv-action${active ? ' is-active' : ''}`} aria-label={label} onClick={onClick} disabled={busy}>
        {busy ? <span className="cv-spinner" aria-hidden="true" /> : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill={active && (icon === 'up' || icon === 'down') ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={ICON[icon]} />
          </svg>
        )}
      </button>
    </Tooltip>
  );
}

/** Per-message actions: thumbs up/down, copy (with references), download, read aloud. */
export default function MessageActions({ message, onVote, vote }) {
  const { t, langOpt } = useChatUi();
  const [copied, setCopied] = useState(false);
  const [speech, setSpeech] = useState('idle'); // idle | loading | playing
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (playing === audioRef.current) playing = null;
    }
  }, []);

  const copy = () => {
    const text = answerWithReferences(message);
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob([`**Q:** ${message.question || ''}\n\n${answerWithReferences(message)}\n`], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `chat-${new Date().toISOString().replace(/[:.]/g, '-')}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const speak = async () => {
    if (speech === 'playing') {
      if (audioRef.current) audioRef.current.pause();
      setSpeech('idle');
      return;
    }
    try {
      setSpeech('loading');
      const res = await axios.post(apiUrl('speech'), { text: textForSpeech(message.text), language: langOpt }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'audio/wav' }));
      if (playing) playing.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      playing = audio;
      audio.onended = () => setSpeech('idle');
      audio.onpause = () => setSpeech('idle');
      await audio.play();
      setSpeech('playing');
    } catch (e) {
      console.error('Error fetching audio:', e);
      setSpeech('idle');
    }
  };

  return (
    <div className="cv-actions">
      <ActionButton label={t('Good response')} icon="up" active={vote === 'up'} onClick={() => onVote && onVote(message, 'up')} />
      <ActionButton label={t('Bad response')} icon="down" active={vote === 'down'} onClick={() => onVote && onVote(message, 'down')} />
      <ActionButton label={copied ? t('Copied') : t('Copy')} icon={copied ? 'check' : 'copy'} onClick={copy} />
      <ActionButton label={t('Save response')} icon="download" onClick={download} />
      <ActionButton
        label={speech === 'playing' ? t('stop') : t('Read aloud')}
        icon={speech === 'playing' ? 'stop' : 'speak'}
        busy={speech === 'loading'}
        onClick={speak}
      />
    </div>
  );
}
